/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@vaas/react-ui-kit/auth';

import {
  getProgramSearchPrograms,
  getVehicleSearchPrograms,
  getVinDetails,
  getVinHistory,
  saveVinHistory,
} from './ediagnostics';
import { getLayoutEngineOptions, getSchematicProperties } from './settings';
import { lookupVehicleMakeModel, resolveProgramForVin } from '@/lib/programMatch';
import type { Program, RecentVehicle, VinDetails, VinHistory } from './models';

/** Caller GMIN; empty when unauthenticated, which disables the VIN history query. */
export function useUserId(): string {
  const { account } = useAuth();
  return account?.gmin ?? '';
}

/** Vehicle context saved when a VIN is opened. */
export interface OpenedVehicle {
  vin: string;
  program?: string;
  programId?: number;
  modelYear?: string;
  make?: string;
  model?: string;
  release?: string;
  /** Diagnostic system (OnStar/SAVI) selected for the search; persisted for live DTC refresh. */
  system?: string;
}

/** Returns a fire-and-forget saver that records an opened VIN and refreshes the recent list. */
export function useSaveVin() {
  const userId = useUserId();
  const { account } = useAuth();
  const queryClient = useQueryClient();
  return useCallback(
    (vehicle: OpenedVehicle) => {
      if (!userId || !vehicle.vin) return;
      const [firstName, ...rest] = (account?.name ?? '').trim().split(' ');
      void saveVinHistory({
        userId,
        ...vehicle,
        firstName: firstName || undefined,
        lastName: rest.join(' ') || undefined,
        email: account?.username ?? undefined,
      })
        .then(() => queryClient.invalidateQueries({ queryKey: ['vin-history', userId] }))
        .catch(() => {});
    },
    [userId, account, queryClient],
  );
}

/** Vehicle-search programs (year/make/model). Fails soft when backend is offline. */
export function usePrograms() {
  return useQuery({
    queryKey: ['programs', 'vehicle-search'],
    queryFn: getVehicleSearchPrograms,
    staleTime: 60_000,
    retry: 1,
    refetchOnMount: 'always',
  });
}

/** Program-search programs (year/program/engineering release + DTC). Distinct endpoint. */
export function useProgramSearch() {
  return useQuery({
    queryKey: ['programs', 'program-search'],
    queryFn: getProgramSearchPrograms,
    staleTime: 60_000,
    retry: 1,
    refetchOnMount: 'always',
  });
}

/**
 * One cache entry per VIN lookup, shared so the deep-link resolver and the DTC badge ask AVP once
 * between them rather than each opening its own request for the same answer.
 */
export function vinDetailsQuery(vin: string, system: string | undefined, dtcs: string[]) {
  return {
    queryKey: ['vin-details', vin, system ?? '', dtcs.join(',')],
    staleTime: 60_000,
    retry: false,
    queryFn: () => getVinDetails(vin, system, dtcs),
  };
}

/**
 * Live DTCs for a VIN.
 *
 * A diagnostic system (OnStar/SAVI) is what lets the backend go and ask. Failing that, codes the
 * referring tool read off the vehicle itself will do: deep-link sources scan before they hand over,
 * and the backend takes their list in place of a lookup it cannot make. With neither there is no
 * question to put, so the query stays idle.
 */
export function useVinDetails(vin?: string, system?: string, dtcs?: string[]) {
  const codes = dtcs ?? [];
  return useQuery<VinDetails | null>({
    ...vinDetailsQuery(vin as string, system, codes),
    enabled: Boolean(vin) && Boolean(system || codes.length),
  });
}

/** User schematic-display prefs; shares TanStack Query cache with Settings dialog. */
export function useSchematicProperties() {
  const gmin = useUserId();
  return useQuery({
    queryKey: ['schematic-properties', gmin],
    enabled: Boolean(gmin),
    retry: false,
    staleTime: 5 * 60_000,
    queryFn: () => getSchematicProperties(gmin),
  });
}

/**
 * Layout engine the caller starts on plus the engines they may pick. Session-long: the audience
 * flags behind it only move when an admin changes them.
 */
export function useLayoutEngineOptions(enabled = true) {
  return useQuery({
    queryKey: ['layout-engine-options'],
    enabled,
    retry: false,
    staleTime: Infinity,
    queryFn: getLayoutEngineOptions,
  });
}

/** Identity + serviceable program resolved live from the API for a single history row. */
interface ResolvedIdentity {
  make?: string;
  model?: string;
  year?: string;
  program?: string;
  programId?: number;
  release?: string;
}

function toRecentVehicle(
  h: VinHistory,
  current: boolean,
  resolved: ResolvedIdentity,
): RecentVehicle {
  const { make, model } = resolved;
  return {
    vin: h.vin,
    model: [make, model].filter(Boolean).join(' ') || 'Vehicle',
    year: resolved.year ?? '',
    program: resolved.program ?? '',
    make,
    rawModel: model,
    programId: resolved.programId,
    release: resolved.release,
    system: h.system,
    timestamp: h.timestamp,
    current,
  };
}

/**
 * Recently opened vehicles, normalized for the home cards.
 *
 * Identity and the serviceable program are always resolved live from the API (AVP for VIN identity +
 * the netlist catalog for the current serviceable program), never from the values cached on the VIN
 * history row. This keeps the cards accurate: a stored program that is now stale can't be shown, and
 * when the API returns no identity / no matching program the card renders as unresolved rather than
 * displaying outdated data.
 *
 * Display priority is VIN > vehicle details (year make model) > program code. Make/model come from
 * AVP; when AVP omits them they are filled from the catalog only when a code+year maps unambiguously
 * to a single make/model (shared platforms stay code-only).
 */
export function useRecentVehicles() {
  const userId = useUserId();
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: ['vin-history', userId],
    enabled: Boolean(userId),
    staleTime: 60_000,
    retry: false,
    queryFn: async (): Promise<RecentVehicle[]> => {
      const history = await getVinHistory(userId);
      if (!history.length) return [];

      const [vehiclePrograms, searchPrograms] = await Promise.all([
        queryClient
          .ensureQueryData<Program[]>({
            queryKey: ['programs', 'vehicle-search'],
            queryFn: getVehicleSearchPrograms,
            staleTime: 5 * 60_000,
          })
          .catch(() => [] as Program[]),
        queryClient
          .ensureQueryData<Program[]>({
            queryKey: ['programs', 'program-search'],
            queryFn: getProgramSearchPrograms,
            staleTime: 5 * 60_000,
          })
          .catch(() => [] as Program[]),
      ]);
      const programs = [...searchPrograms, ...vehiclePrograms];

      return Promise.all(
        history.map(async (h, i) => {
          // VIN identity is immutable, so it is safe to cache; the serviceable program is always
          // re-derived from the current catalog below rather than read from the stored row.
          const details = await queryClient
            .ensureQueryData<VinDetails | null>({
              queryKey: ['vin-identity', h.vin],
              queryFn: () => getVinDetails(h.vin),
              staleTime: 10 * 60_000,
            })
            .catch(() => null);

          const matched = resolveProgramForVin(programs, details);
          let make = details?.make;
          let model = details?.model;
          const year = details?.modelYear;
          const program = matched?.programCode ?? details?.programCode;
          const programId = matched?.id;
          const release = matched?.engineeringRelease;

          // Fill make/model from the catalog when a code+year maps unambiguously to one vehicle.
          if ((!make || !model) && program && year) {
            const mm = lookupVehicleMakeModel(programs, program, year);
            make = make || mm.make;
            model = model || mm.model;
          }

          return toRecentVehicle(h, i === 0, { make, model, year, program, programId, release });
        }),
      );
    },
  });
}
